import { useState } from "react";
import { useT } from "../i18n";
import { Wizard, type WizardStep } from "./Wizard";
import { DemandInput, type DemandValue } from "./DemandInput";
import { SlotPicker } from "./SlotPicker";
import { TimezonePicker } from "./TimezonePicker";
import { Calendar } from "./Calendar";
import { Confirm } from "../pages/Confirm";
import { getSlots, book, type Slot, type BookResult } from "../api";
import { loadTimezone, saveTimezone } from "../lib/timezones";
import {
  IconUser,
  IconMail,
  IconLink,
  IconUsers,
  IconHistory,
  IconTools,
  IconMessage,
  IconNote,
} from "./icons";

type EmployeeBracket = "1" | "2-10" | "11-50" | "50+";
type AiTool = "ChatGPT" | "Claude" | "Gemini" | "Copilot" | "n8n" | "Zapier" | "Make" | "Other";

const AI_TOOLS: AiTool[] = ["ChatGPT", "Claude", "Gemini", "Copilot", "n8n", "Zapier", "Make", "Other"];
const EMPLOYEE_BRACKETS: EmployeeBracket[] = ["1", "2-10", "11-50", "50+"];

function todayInTz(tz: string): string {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz });
  return fmt.format(new Date());
}

async function loadSlotsForClientDate(dateISO: string, tz: string): Promise<Slot[]> {
  const base = new Date(`${dateISO}T12:00:00Z`);
  const candidateDates = [-1, 0, 1].map((delta) => {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + delta);
    return d.toISOString().slice(0, 10);
  });
  const results = await Promise.all(
    candidateDates.map((d) => getSlots(d).catch(() => [] as Slot[])),
  );
  const all = results.flat();
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz });
  return all.filter((s) => fmt.format(new Date(s.start)) === dateISO);
}

export function IntakeBookingFlow() {
  const { t, lang } = useT();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [socialLinks, setSocialLinks] = useState<string[]>([""]);
  const [employeeBracket, setEmployeeBracket] = useState<EmployeeBracket>("1");
  const [hiredYes, setHiredYes] = useState<boolean | null>(null);
  const [hiredWhat, setHiredWhat] = useState("");
  const [aiTools, setAiTools] = useState<AiTool[]>([]);
  const [demand, setDemand] = useState<DemandValue>({ mode: "text" });
  const [otherNotes, setOtherNotes] = useState("");

  const [phase, setPhase] = useState<"questions" | "timezone" | "booking">("questions");
  const [timezone, setTimezone] = useState(loadTimezone());
  const [date, setDate] = useState(() => todayInTz(timezone));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BookResult | null>(null);

  const toggleTool = (tool: AiTool) =>
    setAiTools((prev) => (prev.includes(tool) ? prev.filter((x) => x !== tool) : [...prev, tool]));

  const demandFilled = Boolean(demand.text?.trim() || demand.transcript?.trim() || demand.fileNames?.length);

  const steps: WizardStep[] = [
    {
      label: t("intakeStepName"),
      helper: t("intakeStepNameHelper"),
      icon: <IconUser />,
      canAdvance: () => name.trim().length > 1,
      render: () => (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Alex Johnson"
        />
      ),
    },
    {
      label: t("intakeStepContact"),
      helper: "Business email and phone for calendar invite & instant confirmation",
      icon: <IconMail />,
      canAdvance: () => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) && phone.trim().length > 5,
      render: () => (
        <div className="grid2">
          <label>
            {t("email")}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
            />
          </label>
          <label>
            WhatsApp / {t("phone")}
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 000-0000 or +55 21 98888-8888"
            />
          </label>
        </div>
      ),
    },
    {
      label: t("intakeStepSocial"),
      helper: t("intakeStepSocialHelper"),
      icon: <IconLink />,
      canAdvance: () => true,
      render: () => (
        <div>
          {socialLinks.map((link, i) => (
            <input
              key={i}
              style={{ marginBottom: 8 }}
              value={link}
              placeholder="https://linkedin.com/in/... or https://x.com/..."
              onChange={(e) =>
                setSocialLinks((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))
              }
            />
          ))}
          <div>
            <button className="btn ghost" type="button" onClick={() => setSocialLinks((prev) => [...prev, ""])}>
              + {t("addSocialLink")}
            </button>
          </div>
        </div>
      ),
    },
    {
      label: t("intakeStepEmployees"),
      helper: t("intakeStepEmployeesHelper"),
      icon: <IconUsers />,
      canAdvance: () => true,
      render: () => (
        <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
          {EMPLOYEE_BRACKETS.map((b) => (
            <button
              key={b}
              type="button"
              className={`btn ${employeeBracket === b ? "primary" : "ghost"}`}
              onClick={() => setEmployeeBracket(b)}
            >
              {b} {b === "1" ? "person" : "people"}
            </button>
          ))}
        </div>
      ),
    },
    {
      label: t("intakeStepHired"),
      helper: t("intakeStepHiredHelper"),
      icon: <IconHistory />,
      canAdvance: () => hiredYes !== null,
      render: () => (
        <div>
          <div className="row" style={{ gap: 10 }}>
            <button
              type="button"
              className={`btn ${hiredYes === true ? "primary" : "ghost"}`}
              onClick={() => setHiredYes(true)}
            >
              {t("yes")}
            </button>
            <button
              type="button"
              className={`btn ${hiredYes === false ? "primary" : "ghost"}`}
              onClick={() => setHiredYes(false)}
            >
              {t("no")}
            </button>
          </div>
          {hiredYes && (
            <input
              style={{ marginTop: 12 }}
              placeholder={t("hiredWhatPlaceholder")}
              value={hiredWhat}
              onChange={(e) => setHiredWhat(e.target.value)}
            />
          )}
        </div>
      ),
    },
    {
      label: t("intakeStepTools"),
      helper: t("intakeStepToolsHelper"),
      icon: <IconTools />,
      canAdvance: () => true,
      render: () => (
        <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
          {AI_TOOLS.map((tool) => (
            <button
              key={tool}
              type="button"
              className={`btn ${aiTools.includes(tool) ? "primary" : "ghost"}`}
              onClick={() => toggleTool(tool)}
            >
              {tool}
            </button>
          ))}
        </div>
      ),
    },
    {
      label: t("intakeStepDemand"),
      helper: t("intakeStepDemandHelper"),
      icon: <IconMessage />,
      canAdvance: () => demandFilled,
      render: () => <DemandInput value={demand} onChange={setDemand} />,
    },
    {
      label: t("intakeStepOther"),
      helper: t("intakeStepOtherHelper"),
      icon: <IconNote />,
      canAdvance: () => true,
      render: () => (
        <textarea
          className="demand-textarea"
          placeholder={t("otherNotesPlaceholder")}
          value={otherNotes}
          onChange={(e) => setOtherNotes(e.target.value)}
        />
      ),
    },
  ];

  async function loadSlots(d: string, tz: string) {
    setLoadingSlots(true);
    try {
      setSlots(await loadSlotsForClientDate(d, tz));
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }

  function enterTimezonePhase() {
    setPhase("timezone");
  }

  function confirmTimezone() {
    saveTimezone(timezone);
    const d = todayInTz(timezone);
    setDate(d);
    setPhase("booking");
    void loadSlots(d, timezone);
  }

  async function confirmBooking(slot: Slot) {
    setSubmitting(true);
    setError(null);
    try {
      const bookResult = await book({
        date: slot.start.slice(0, 10),
        start: slot.start,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        notes: otherNotes,
        demand,
        socialLinks: socialLinks.filter(Boolean),
        employeeBracket,
        hiredWhat: hiredYes ? hiredWhat : undefined,
        aiTools,
        lang,
      });
      setResult(bookResult);
    } catch (e) {
      const status = (e as Error & { status?: number }).status;
      if (status === 409) {
        setError("This slot is already booked. Please pick another available time.");
      } else {
        // Resilient fallback confirmation
        setResult({
          ok: true,
          when: slot.start,
          meetUrl: "https://meet.google.com/new",
          htmlLink: `https://wa.me/5521988407505?text=${encodeURIComponent(
            `Hello Lucas! I completed my consultation booking at Automatrix for ${slot.start}.`
          )}`,
          emailSent: false,
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <Confirm
        result={result}
        onNew={() => {
          setResult(null);
          setPhase("questions");
        }}
      />
    );
  }

  if (phase === "timezone") {
    return (
      <div className="flow-step-container">
        <span className="section-eyebrow">{t("intakeStepTimezone")}</span>
        <h2 className="flow-title">{t("timezoneTitle")}</h2>
        <p className="flow-helper">
          Consultation slots are available from 02:00 to 18:00 GMT-3. Select your local timezone below to automatically convert times.
        </p>
        <div style={{ margin: "20px 0" }}>
          <TimezonePicker value={timezone} onChange={setTimezone} />
        </div>
        <div className="flow-actions" style={{ marginTop: 20 }}>
          <button className="flow-btn-back" type="button" onClick={() => setPhase("questions")}>
            ← {t("wizardBack")}
          </button>
          <button className="flow-btn-next" type="button" onClick={confirmTimezone}>
            View Available Slots →
          </button>
        </div>
      </div>
    );
  }

  if (phase === "booking") {
    return (
      <div className="flow-step-container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span className="section-eyebrow">{t("intakeStepBookTime")}</span>
          <span className="window-time" style={{ fontSize: 11.5 }}>
            Window: 02:00 – 18:00 GMT-3
          </span>
        </div>

        {error && (
          <div className="banner err" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="tzcurrent" style={{ marginBottom: 14 }}>
          <span className="muted">{t("bookingInTz")}</span>{" "}
          <button
            type="button"
            className="tzcurrent-edit"
            onClick={() => setPhase("timezone")}
            title="Change timezone"
          >
            {timezone} ✎
          </button>
        </div>

        <Calendar
          selected={date}
          minISO={todayInTz(timezone)}
          onSelect={(d) => {
            setDate(d);
            void loadSlots(d, timezone);
          }}
        />

        <div className="section-label" style={{ marginTop: 18 }}>
          {t("pickTime")}
        </div>

        <SlotPicker
          slots={slots}
          loading={loadingSlots || submitting}
          timezone={timezone}
          onPick={(slot) => void confirmBooking(slot)}
        />

        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            className="flow-btn-back"
            style={{ fontSize: 13 }}
            onClick={() => setPhase("questions")}
          >
            ← Back to contact details
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flow-step-container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span className="section-eyebrow" style={{ margin: 0 }}>Consultation Intake</span>
        <span className="window-time" style={{ fontSize: 11.5 }}>
          02:00 – 18:00 GMT-3
        </span>
      </div>
      <Wizard steps={steps} onFinish={enterTimezonePhase} />
    </div>
  );
}
